'use client';;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:synergiefrontends":true});}catch(e){}}();

/* eslint-disable eqeqeq */
import React, { ReactElement } from 'react';
import { Pagination, PaginationItem, PaginationLink } from 'reactstrap';
import useWindowWidth from '../hooks/useWindowWidth';

function Pager({
  currentPage = 1,
  changePage,
  className,
  totalPages,
  maxPages = 7,
}: {
  currentPage?: any;
  changePage?: (newPage: number) => void;
  className?: string;
  totalPages: number;
  maxPages?: number;
}) {
  const currentPageNumber = parseInt(currentPage);

  const windowWidth = useWindowWidth();
  const onMobile = windowWidth && windowWidth < 400;

  // Do not display page buttons if there is only one page
  const showPageButtons = totalPages > 1;

  // This array will hold all buttons in the Pager
  const paginationItems: ReactElement[] = [];

  // '<' button: visible unless we are on the first page
  const showBackButton = currentPageNumber > 1;
  if (showBackButton) {
    paginationItems.push(
      <PaginationItem key="prev">
        <PaginationLink onClick={() => changePage?.(currentPageNumber - 1)} previous />
      </PaginationItem>
    );
  }

  // Page buttons (1-2-3...): for every page, this function decides if a button is displayed
  const isPageButtonVisible = (pageNr) => {
    // If the total nr of pages is <= maxPages then all buttons are visible
    if (totalPages <= maxPages) return true;

    // First and last page are always visible
    if (pageNr == 1 || pageNr == totalPages) return true;

    // Current page, and the pages right before and after it are always visible on desktop
    // For example: if we're on page 5, then pages 4, 5 and 6 are visible
    // On mobile we skip the pages right before and after the current page
    if (
      pageNr == currentPageNumber ||
      (pageNr == currentPageNumber - 1 && !onMobile) ||
      (pageNr == currentPageNumber + 1 && !onMobile)
    )
      return true;

    // On mobile, pages right before should be visible if current page is last or before last
    // Page right after should be visible if current page is first or second
    if (
      (pageNr == currentPageNumber - 1 && currentPageNumber >= totalPages - 1 && onMobile) ||
      (pageNr == currentPageNumber + 1 && currentPageNumber <= 2 && onMobile)
    )
      return true;

    // If hiding this button would only hide 1 page... just show the page itself
    // AKA: If the distance between the previous page and page 1 is just 1
    // AKA: If the distance between the next page and the last page is just 1
    if (currentPageNumber <= 4 && pageNr < currentPageNumber && !onMobile) return true;
    if (currentPageNumber >= totalPages - 3 && pageNr > currentPageNumber && !onMobile) return true;

    // The page button is not visible
    return false;
  };

  // Three Dots: Displayed as a substitute for a range of (2 or more) pages,
  // this function determines if it should be displayed at a given position
  const isThreeDotsVisible = (pageNr) => {
    // Left three dots are visible when currentPageNumber is > 4
    // For example: [1 ... 4-5-6]: the ellipsis hides pages 2 and 3
    if (pageNr == 3 && currentPageNumber > 4) return true;

    // Right three dots are visible when currentPageNumber is < totalPages - 3
    // For example: [5-6-7 ... 10]: the ellipsis hides pages 8 and 9
    if (pageNr == totalPages - 2 && currentPageNumber < totalPages - 3 && !onMobile) return true;

    // On mobile, as we do not show the pages right before or right after the current page, we should show ellipsis for the previous to last page as well (unless we're on the last page)
    if (onMobile && pageNr == totalPages - 1 && currentPageNumber != totalPages) return true;

    // On mobile, as we do not show the pages right before or right after the current page, we should show ellipsis for the second page as well
    if (onMobile && pageNr == 2 && currentPageNumber < 5) return true;
  };

  // For every pageNr, render either a button, three dots, or nothing at all
  for (let i = 1; i <= totalPages; i += 1) {
    if (isPageButtonVisible(i)) {
      paginationItems.push(
        <PaginationItem key={i} active={i === currentPageNumber}>
          <PaginationLink onClick={() => changePage?.(i)}>{i}</PaginationLink>
        </PaginationItem>
      );
    } else if (isThreeDotsVisible(i)) {
      paginationItems.push(
        <PaginationItem disabled key={i}>
          <PaginationLink>...</PaginationLink>
        </PaginationItem>
      );
    } else {
      // Nothing rendered!
    }
  }

  // Next button: Always visible, but disabled on the last page
  const showNextButton = currentPageNumber < totalPages;
  if (showNextButton) {
    paginationItems.push(
      <PaginationItem key="next">
        <PaginationLink onClick={() => changePage?.(currentPageNumber + 1)} next />
      </PaginationItem>
    );
  }

  return (
    <div className={`pager ${className || ''}`}>
      {showPageButtons && <Pagination aria-label="job search navigation">{paginationItems}</Pagination>}
    </div>
  );
}

export default Pager;
